import React from 'react';
import { Link } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import usePageContent from '../hooks/usePageContent';
import { formatPageContent } from '../utils/formatPageContent';

/**
 * PrivacyPolicyPage - Displays privacy policy content from API or fallback
 */
const PrivacyPolicyPage = () => {
  const { page, loading, error } = usePageContent('privacy-policy');

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen text-white bg-gray-900">
        <Header />
        <main className="container max-w-4xl px-4 py-12 mx-auto">
          <div className="text-center mb-12">
            <div className="h-12 w-64 mx-auto bg-gray-700 rounded animate-pulse mb-4"></div>
            <div className="h-4 w-48 mx-auto bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-700 rounded animate-pulse w-5/6"></div>
            <div className="h-4 bg-gray-700 rounded animate-pulse w-4/6"></div>
            <div className="h-32 bg-gray-800 rounded-lg animate-pulse mt-8"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // If we have API content, render it
  if (page && (page.body || page.html)) {
    const content = formatPageContent(page.body || page.html);
    const title = page.page_name || page.nav_name || 'Privacy Policy';
    const updatedAt = page.updated_at
      ? new Date(page.updated_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null;

    return (
      <div className="min-h-screen text-white bg-gray-900">
        <Header />
        <main className="container max-w-4xl px-4 py-12 mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text text-transparent">
              {title}
            </h1>
            {updatedAt && <p className="text-gray-400">Effective as of {updatedAt}</p>}
          </div>

          {/* Dynamic Content */}
          <div
            className="prose prose-lg prose-invert max-w-none dynamic-page-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </main>
        <Footer />
      </div>
    );
  }

  // Fallback to static content
  return (
    <div className="min-h-screen text-white bg-gray-900">
      <Header />

      <main className="container max-w-4xl px-4 py-12 mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          <p className="text-gray-400">Effective as of February 23, 2024</p>
        </div>

        {/* Table of Contents */}
        <div className="p-6 mb-12 bg-gray-800 border border-gray-700 rounded-lg">
          <h2 className="text-xl font-semibold mb-4 text-[#9ec6f0]">Table of Contents</h2>
          <div className="grid gap-2 text-sm md:grid-cols-2">
            <a
              href="#introduction"
              className="text-gray-300 hover:text-[#9ec6f0] transition-colors"
            >
              1. Introduction
            </a>
            <a
              href="#information-collection"
              className="text-gray-300 hover:text-[#9ec6f0] transition-colors"
            >
              2. Information We Collect
            </a>
            <a href="#how-we-use" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              3. How We Use Information
            </a>
            <a href="#sharing" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              4. Information Sharing
            </a>
            <a href="#cookies" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              5. Cookies and Tracking
            </a>
            <a href="#security" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              6. Data Security
            </a>
            <a href="#your-rights" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              7. Your Rights
            </a>
            <a
              href="#state-specific"
              className="text-gray-300 hover:text-[#9ec6f0] transition-colors"
            >
              8. State-Specific Rights
            </a>
            <a href="#children" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              9. Children's Privacy
            </a>
            <a
              href="#international"
              className="text-gray-300 hover:text-[#9ec6f0] transition-colors"
            >
              10. International Users
            </a>
            <a href="#changes" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              11. Policy Changes
            </a>
            <a href="#contact" className="text-gray-300 hover:text-[#9ec6f0] transition-colors">
              12. Contact Us
            </a>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          {/* Introduction */}
          <section id="introduction" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">1. Introduction</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                This privacy policy ("Privacy Policy") provides a detailed description of how
                Dotstudioz, INC and its affiliates including, but not limited to dotstudioPRO,
                ElectricNOW ("us," "we," or "our") collect, use, and share information about you as
                well as your rights and choices regarding such information.
              </p>
              <p className="mb-4 leading-relaxed text-gray-300">
                This Privacy Policy applies to any online location, mobile applications, interactive
                TV applications, desktop applications, digital media players, and other digital
                services that link to this Privacy Policy (the "Service") including the following:
              </p>
              <ul className="mb-4 space-y-1 text-gray-300 list-disc list-inside">
                <li>electricnow.tv</li>
                <li>dotstudiopro.com</li>
                <li>corp.dotstudiopro.com</li>
                <li>dotstudioz.com</li>
                <li>electricnow.net</li>
                <li>other Dotstudioz owned websites</li>
              </ul>
              <p className="leading-relaxed text-gray-300">
                By using our Service, you consent to the applicable terms of use for the respective
                Service and agree to the collection and use of information from or about you as
                explained in this Privacy Policy. If you do not agree and consent to the collection
                and use of information from or about you in accordance with this Privacy Policy, then
                you may not use and/or must discontinue use of the Service.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section id="information-collection" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">2. Information We Collect</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-[#9ec6f0]">Personal Information</h3>
                <p className="mb-4 leading-relaxed text-gray-300">
                  We collect personally identifiable information that you voluntarily provide to us,
                  including:
                </p>
                <ul className="space-y-1 text-gray-300 list-disc list-inside">
                  <li>Email address</li>
                  <li>Full name</li>
                  <li>Mailing address</li>
                  <li>Phone numbers (work, cell, and home)</li>
                  <li>Account preferences and settings</li>
                  <li>Communication preferences</li>
                </ul>
              </div>

              <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-[#9ec6f0]">Usage Information</h3>
                <p className="mb-4 leading-relaxed text-gray-300">
                  We automatically collect information about how you use our Service, including:
                </p>
                <ul className="space-y-1 text-gray-300 list-disc list-inside">
                  <li>Viewing history and preferences</li>
                  <li>Device information and identifiers</li>
                  <li>IP address and location data</li>
                  <li>Browser type and version</li>
                  <li>Operating system information</li>
                  <li>Referral sources and website navigation patterns</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section id="how-we-use" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">3. How We Use Information</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                We use the information we collect for various purposes, including:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Service Provision</h4>
                  <ul className="space-y-1 text-sm text-gray-300 list-disc list-inside">
                    <li>Providing and maintaining our Service</li>
                    <li>Processing your requests and transactions</li>
                    <li>Personalizing your experience</li>
                    <li>Providing customer support</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Communication</h4>
                  <ul className="space-y-1 text-sm text-gray-300 list-disc list-inside">
                    <li>Sending service-related notifications</li>
                    <li>Responding to your inquiries</li>
                    <li>Sending promotional materials (with consent)</li>
                    <li>Conducting surveys and research</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Improvement</h4>
                  <ul className="space-y-1 text-sm text-gray-300 list-disc list-inside">
                    <li>Analyzing usage patterns</li>
                    <li>Improving our Service and content</li>
                    <li>Developing new features</li>
                    <li>Troubleshooting technical issues</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Legal Compliance</h4>
                  <ul className="space-y-1 text-sm text-gray-300 list-disc list-inside">
                    <li>Complying with legal obligations</li>
                    <li>Protecting our rights and property</li>
                    <li>Ensuring user safety and security</li>
                    <li>Preventing fraud and abuse</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section id="sharing" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">4. Information Sharing</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                We may share your information in the following circumstances:
              </p>
              <div className="space-y-4">
                <div className="border-l-4 border-[#2f6eb0] pl-4">
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">With Your Consent</h4>
                  <p className="text-sm text-gray-300">
                    We may share your information with third parties when you have given us explicit
                    consent to do so.
                  </p>
                </div>
                <div className="pl-4 border-l-4 border-yellow-500">
                  <h4 className="mb-2 font-semibold text-yellow-400">Service Providers</h4>
                  <p className="text-sm text-gray-300">
                    We may share information with trusted service providers who assist us in
                    operating our Service, subject to confidentiality agreements.
                  </p>
                </div>
                <div className="pl-4 border-l-4 border-red-500">
                  <h4 className="mb-2 font-semibold text-red-400">Legal Requirements</h4>
                  <p className="text-sm text-gray-300">
                    We may disclose information when required by law, court order, or to protect our
                    rights, property, or safety.
                  </p>
                </div>
                <div className="pl-4 border-l-4 border-blue-500">
                  <h4 className="mb-2 font-semibold text-blue-400">Business Transfers</h4>
                  <p className="text-sm text-gray-300">
                    In the event of a merger, acquisition, or sale of assets, your information may be
                    transferred as part of the transaction.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Cookies and Tracking */}
          <section id="cookies" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">
              5. Cookies and Tracking Technologies
            </h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                We use cookies and similar tracking technologies to enhance your experience on our
                Service. Cookies are small data files stored on your device that help us:
              </p>
              <ul className="mb-4 space-y-1 text-gray-300 list-disc list-inside">
                <li>Remember your preferences and settings</li>
                <li>Provide personalized content recommendations</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve our Service performance and functionality</li>
                <li>Deliver relevant advertisements</li>
              </ul>
              <p className="leading-relaxed text-gray-300">
                You can control cookie settings through your browser preferences. However, disabling
                cookies may limit some functionality of our Service. Third-party advertisers may also
                use cookies, and we recommend reviewing their privacy policies for more information.
              </p>
            </div>
          </section>

          {/* Data Security */}
          <section id="security" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">6. Data Security</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                We implement reasonable administrative, technical, personnel and physical measures to
                safeguard information in our possession against loss, theft and unauthorized use,
                disclosure or modification. Our security measures include:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Technical Safeguards</h4>
                  <ul className="space-y-1 text-sm text-gray-300 list-disc list-inside">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Secure server infrastructure</li>
                    <li>Regular security updates and patches</li>
                    <li>Firewall and intrusion detection systems</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Administrative Controls</h4>
                  <ul className="space-y-1 text-sm text-gray-300 list-disc list-inside">
                    <li>Access controls and user authentication</li>
                    <li>Employee training on data protection</li>
                    <li>Regular security audits and assessments</li>
                    <li>Incident response procedures</li>
                  </ul>
                </div>
              </div>
              <p className="mt-4 leading-relaxed text-gray-300">
                While we strive to protect your information, no method of transmission over the
                internet or electronic storage is 100% secure. We cannot guarantee absolute security
                but are committed to protecting your data using industry-standard practices.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section id="your-rights" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">7. Your Rights and Choices</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                You have certain rights regarding your personal information:
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#2f6eb0] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#9ec6f0]">Access and Portability</h4>
                    <p className="text-sm text-gray-300">
                      Request access to your personal information and receive a copy in a portable
                      format.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#2f6eb0] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#9ec6f0]">Correction</h4>
                    <p className="text-sm text-gray-300">
                      Request correction of inaccurate or incomplete personal information.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#2f6eb0] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#9ec6f0]">Deletion</h4>
                    <p className="text-sm text-gray-300">
                      Request deletion of your personal information, subject to certain exceptions.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#2f6eb0] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs text-white">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#9ec6f0]">Opt-Out</h4>
                    <p className="text-sm text-gray-300">
                      Opt out of marketing communications and certain data processing activities.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-teal-900/20 border border-[#2f6eb0]/30 rounded-lg">
                <p className="text-sm text-gray-300">
                  To exercise your rights, please contact us at{' '}
                  <a
                    href="mailto:datarequest@electricnow.tv"
                    className="text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    datarequest@electricnow.tv
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* State-Specific Rights */}
          <section id="state-specific" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">
              8. State-Specific Privacy Rights
            </h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                If you are a resident of California, Colorado, Connecticut, Utah, or Virginia, you
                may have additional privacy rights under state law:
              </p>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg border-blue-500/30">
                  <h4 className="mb-2 font-semibold text-blue-400">
                    California Residents (CCPA/CPRA)
                  </h4>
                  <p className="mb-2 text-sm text-gray-300">
                    California residents have specific rights under the California Consumer Privacy
                    Act (CCPA) and California Privacy Rights Act (CPRA).
                  </p>
                  <div className="flex space-x-4">
                    <Link
                      to="/ccpa"
                      className="text-[#9ec6f0] hover:text-[#b3cfed] underline text-sm"
                    >
                      Learn More About CCPA Rights
                    </Link>
                    <Link
                      to="/do-not-sell"
                      className="text-[#9ec6f0] hover:text-[#b3cfed] underline text-sm"
                    >
                      Do Not Sell My Data
                    </Link>
                  </div>
                </div>
                <div className="p-4 border rounded-lg border-purple-500/30">
                  <h4 className="mb-2 font-semibold text-purple-400">Other State Residents</h4>
                  <p className="text-sm text-gray-300">
                    Residents of Colorado, Connecticut, Utah, and Virginia may have similar rights
                    under their respective state privacy laws. Contact us for more information about
                    your specific rights.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Children's Privacy */}
          <section id="children" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">9. Children's Privacy</h2>
            <div className="p-6 border rounded-lg bg-yellow-900/20 border-yellow-500/30">
              <p className="mb-4 leading-relaxed text-gray-300">
                Our Service is not intended for children under the age of 13. We do not knowingly
                collect personal information from children under 13. If you are a parent or guardian
                and believe your child has provided us with personal information, please contact us
                immediately.
              </p>
              <p className="leading-relaxed text-gray-300">
                If we become aware that we have collected personal information from a child under 13
                without verification of parental consent, we will take steps to remove that
                information from our servers.
              </p>
            </div>
          </section>

          {/* International Users */}
          <section id="international" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">10. International Users</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                Our Service is intended for users in the United States. If you are located outside of
                the United States and are accessing our Service, your personal information will be
                processed and stored in the United States.
              </p>
              <p className="leading-relaxed text-gray-300">
                The laws in the United States may not be as protective of personal information as
                laws in your resident country. If you do not agree with the processing of your
                personal information in the United States, please discontinue your use of the
                Service.
              </p>
            </div>
          </section>

          {/* Policy Changes */}
          <section id="changes" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">
              11. Changes to This Privacy Policy
            </h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                We may update this Privacy Policy from time to time to reflect changes in our
                practices, technology, legal requirements, or other factors. When we make changes, we
                will:
              </p>
              <ul className="mb-4 space-y-1 text-gray-300 list-disc list-inside">
                <li>Update the "Effective Date" at the top of this policy</li>
                <li>Notify you of material changes through our Service or by email</li>
                <li>Provide you with an opportunity to review the updated policy</li>
              </ul>
              <p className="leading-relaxed text-gray-300">
                Your continued use of our Service after any changes indicates your acceptance of the
                updated Privacy Policy.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section id="contact" className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">12. Contact Us</h2>
            <div className="bg-teal-900/20 border border-[#2f6eb0]/30 rounded-lg p-6">
              <p className="mb-4 leading-relaxed text-gray-300">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our
                data practices, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-gray-300">
                  <strong>Email:</strong>{' '}
                  <a
                    href="mailto:datarequest@electricnow.tv"
                    className="text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    datarequest@electricnow.tv
                  </a>
                </p>
                <p className="text-gray-300">
                  <strong>General Support:</strong>{' '}
                  <a
                    href="mailto:support@electricnow.tv"
                    className="text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    support@electricnow.tv
                  </a>
                </p>
                <p className="text-gray-300">
                  <strong>Company:</strong> Dotstudioz INC
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-[#2f6eb0]/30">
                <p className="text-sm text-gray-400">
                  We will respond to your inquiry within 30 business days of receipt.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
